import React, { useState, useEffect, useCallback } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  IconButton,
  Divider,
} from '@mui/material'
import { Close as CloseIcon } from '@mui/icons-material'
import { getInvoiceById, fetchOrganizationDetails } from '../../../services/invoiceRecords.service'
import InvoicePdfPreview from './InvoicePdfPreview'

/**
 * Modal to display invoice as PDF preview
 * Shows the invoice in the same format as PlantBusinessView but read-only
 * Includes download button
 */
const RemarkModal = ({ open, onClose, invoiceId }) => {
  const [invoice, setInvoice] = useState(null)
  const [organizationData, setOrganizationData] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  const loadInvoiceDetails = useCallback(async () => {
    if (!invoiceId) return

    try {
      setLoading(true)
      setError(null)

      const response = await getInvoiceById(invoiceId)
      console.log('Invoice response:', response)

      if (response.success && response.data) {
        const invoiceData = response.data
        setInvoice(invoiceData)

        // Fetch organization data if organizationId exists
        if (invoiceData.organizationId) {
          try {
            const orgDetails = await fetchOrganizationDetails(invoiceData.organizationId)
            setOrganizationData(orgDetails)
          } catch (orgError) {
            console.error('Error fetching organization:', orgError)
          }
        }
      } else {
        setError(response.message || 'Failed to load invoice details')
      }
    } catch (err) {
      console.error('Error loading invoice:', err)
      setError('Failed to load invoice details')
    } finally {
      setLoading(false)
    }
  }, [invoiceId])

  useEffect(() => {
    if (open && invoiceId) {
      loadInvoiceDetails()
    } else if (!open) {
      // Reset state when modal closes
      setInvoice(null)
      setOrganizationData(null)
      setError(null)
    }
  }, [open, invoiceId, loadInvoiceDetails])

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          minHeight: '80vh',
          maxHeight: '95vh',
        },
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6" fontWeight={600}>
            Invoice Preview
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <Divider />

      <DialogContent sx={{ p: 3 }}>
        {loading && (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
            <Typography color="error">{error}</Typography>
          </Box>
        )}

        {!loading && !error && (
          <InvoicePdfPreview
            invoice={invoice}
            organizationData={organizationData}
            loading={loading}
          />
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default RemarkModal
